/**
 * React Query hooks for clinical trends data
 */
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  getFEV1Trends,
  getSixMWTTrends,
  getVitalsTrends,
  getCombinedTrends,
  getMyCombinedTrends,
  FEV1TrendResponse,
  SixMWTTrendResponse,
  VitalsTrendResponse,
  CombinedTrendResponse
} from '../api/trends';

// ==================== Query Keys ====================

export const trendKeys = {
  all: ['trends'] as const,
  patient: (patientId: number) => [...trendKeys.all, patientId] as const,
  fev1: (patientId: number, weeks: number) => 
    [...trendKeys.patient(patientId), 'fev1', weeks] as const,
  sixmwt: (patientId: number, weeks: number) => 
    [...trendKeys.patient(patientId), 'sixmwt', weeks] as const,
  vitals: (patientId: number, days: number, metric: string) => 
    [...trendKeys.patient(patientId), 'vitals', days, metric] as const,
  combined: (patientId: number, weeks: number) => 
    [...trendKeys.patient(patientId), 'combined', weeks] as const,
};

// ==================== Hooks ====================

/**
 * Hook to fetch FEV1 trend data
 */
export function useFEV1Trends(
  patientId: number,
  weeks: number = 6,
  options?: Omit<UseQueryOptions<FEV1TrendResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: trendKeys.fev1(patientId, weeks),
    queryFn: () => getFEV1Trends(patientId, weeks),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

/**
 * Hook to fetch 6MWT trend data
 */
export function useSixMWTTrends(
  patientId: number,
  weeks: number = 6,
  options?: Omit<UseQueryOptions<SixMWTTrendResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: trendKeys.sixmwt(patientId, weeks),
    queryFn: () => getSixMWTTrends(patientId, weeks),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

/**
 * Hook to fetch vitals trend data
 */
export function useVitalsTrends(
  patientId: number,
  days: number = 30,
  metric: string = 'all',
  options?: Omit<UseQueryOptions<VitalsTrendResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: trendKeys.vitals(patientId, days, metric),
    queryFn: () => getVitalsTrends(patientId, days, metric),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

/**
 * Hook to fetch combined clinical trends (optimized for dashboards)
 * This fetches FEV1, 6MWT, and SpO2 in a single request
 */
export function useCombinedTrends(
  patientId: number,
  weeks: number = 6,
  enabled: boolean = true,
  options?: Omit<UseQueryOptions<CombinedTrendResponse>, 'queryKey' | 'queryFn' | 'enabled'>
) {
  return useQuery({
    queryKey: trendKeys.combined(patientId, weeks),
    queryFn: () => getCombinedTrends(patientId, weeks),
    enabled: enabled && !!patientId && patientId > 0, // Only fetch if patientId is valid
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refetch every 5 minutes
    ...options
  });
}

/**
 * Hook to fetch combined clinical trends for the current user (optimized for dashboards)
 * This fetches FEV1, 6MWT, and SpO2 in a single request
 */
export function useMyCombinedTrends(
  weeks: number = 6,
  enabled: boolean = true,
  options?: Omit<UseQueryOptions<CombinedTrendResponse>, 'queryKey' | 'queryFn' | 'enabled'>
) {
  return useQuery({
    queryKey: ['my', ...trendKeys.combined(0, weeks)],
    queryFn: () => getMyCombinedTrends(weeks),
    enabled: enabled, // Only fetch if enabled is true
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refetch every 5 minutes
    ...options
  });
}